import { useState, useEffect, useCallback, useRef } from "react";
import * as tf from "@tensorflow/tfjs";

const MODEL_KEY    = "indexeddb://digitair-delay-model";
const OLD_MODEL_KEY = "localstorage://digitair-delay-model";
const VOCAB_KEY    = "digitair-delay-vocab";
const METRICS_KEY  = "digitair-delay-metrics";
const MAX_DELAY    = 180;
export const DELAY_EPOCHS = 100;
const FEATURE_NAMES = ["Weekday", "Month", "STD", "Origin", "Arrival", "AC Type", "Flight Reg"];

function buildFeature(record, vocab) {
  const { depPorts, arrPorts, acTypes, flightRegs } = vocab;
  const depIdx  = depPorts.indexOf(record.dep);
  const arrIdx  = arrPorts.indexOf(record.arr);
  const acIdx   = acTypes.indexOf(record.acType);
  const regIdx  = flightRegs.indexOf(record.flightReg);
  const stdMins = (record.stdHour || 0) * 60 + (record.stdMinute || 0);
  return [
    record.dayOfWeek / 6,
    record.month     / 11,
    stdMins          / 1439,
    depIdx < 0 ? 0 : depIdx / Math.max(depPorts.length - 1, 1),
    arrIdx < 0 ? 0 : arrIdx / Math.max(arrPorts.length - 1, 1),
    acIdx  < 0 ? 0 : acIdx  / Math.max(acTypes.length  - 1, 1),
    regIdx < 0 ? 0 : regIdx / Math.max(flightRegs.length - 1, 1),
  ];
}

function buildModel() {
  const model = tf.sequential({
    layers: [
      tf.layers.dense({ inputShape: [7], units: 32, activation: "relu" }),
      tf.layers.dropout({ rate: 0.1 }),
      tf.layers.dense({ units: 16, activation: "relu" }),
      tf.layers.dense({ units: 2, activation: "sigmoid" }),
    ],
  });
  model.compile({
    optimizer: tf.train.adam(0.01),
    loss: "meanSquaredError",
    metrics: ["mae"],
  });
  return model;
}

export function useDelayModel() {
  const [model, setModel]               = useState(null);
  const [isModelReady, setIsModelReady] = useState(false);
  const [isTraining, setIsTraining]     = useState(false);
  const [trainingLogs, setTrainingLogs] = useState([]);
  const [vocab, setVocab]               = useState(null);
  const [lastMetrics, setLastMetrics]   = useState(null);
  const trainingDataRef                 = useRef(null);
  const lastLogRef                      = useRef(null);
  const workerRef                       = useRef(null);

  useEffect(() => {
    const loadSaved = async () => {
      // Migrate: remove old localstorage model if exists
      try { await tf.io.removeModel(OLD_MODEL_KEY); } catch {}

      try {
        const savedVocab   = localStorage.getItem(VOCAB_KEY);
        const savedMetrics = localStorage.getItem(METRICS_KEY);
        if (!savedVocab) return;
        const parsedVocab = JSON.parse(savedVocab);
        // Reject old models trained without flightRegs (architecture mismatch)
        if (!parsedVocab.flightRegs) {
          localStorage.removeItem(VOCAB_KEY);
          localStorage.removeItem(METRICS_KEY);
          try { await tf.io.removeModel(MODEL_KEY); } catch {}
          return;
        }
        const m = await tf.loadLayersModel(MODEL_KEY);
        setModel(m);
        setVocab(parsedVocab);
        if (savedMetrics) setLastMetrics(JSON.parse(savedMetrics));
        setIsModelReady(true);
      } catch (err) {
        console.warn("Delay model: failed to load from storage:", err);
      }
    };
    loadSaved();
    return () => { workerRef.current?.terminate(); };
  }, []);

  const trainModel = useCallback(async (rawData) => {
    if (!rawData || rawData.length < 10) return false;
    setIsTraining(true);
    setTrainingLogs([]);
    trainingDataRef.current = rawData;

    const depPorts   = [...new Set(rawData.map((r) => r.dep))].sort();
    const arrPorts   = [...new Set(rawData.map((r) => r.arr))].sort();
    const acTypes    = [...new Set(rawData.map((r) => r.acType))].sort();
    const flightRegs = [...new Set(rawData.map((r) => r.flightReg).filter(Boolean))].sort();
    const newVocab   = { depPorts, arrPorts, acTypes, flightRegs };

    const features = rawData.map((r) => buildFeature(r, newVocab));
    const labels   = rawData.map((r) => [
      Math.min(r.depDelayMinutes, MAX_DELAY) / MAX_DELAY,
      Math.min(r.arrDelayMinutes, MAX_DELAY) / MAX_DELAY,
    ]);

    return new Promise((resolve) => {
      workerRef.current?.terminate();
      // eslint-disable-next-line no-undef
      const worker = new Worker(new URL('./workers/tfTrainWorker.js', import.meta.url));
      workerRef.current = worker;

      worker.onmessage = async ({ data: msg }) => {
        try {
          if (msg.type === 'epoch') {
            lastLogRef.current = msg.payload;
            setTrainingLogs((prev) => [...prev, msg.payload]);
            return;
          }
          worker.terminate();
          workerRef.current = null;
          setIsTraining(false);

          if (msg.type === 'error') {
            console.error("Delay model worker error:", msg.payload.message);
            resolve(false);
            return;
          }

          // Reconstruct model in main thread (uses WebGL for fast inference)
          const m       = buildModel();
          const weights = msg.payload.weightSpecs.map(({ data, shape, dtype }) =>
            tf.tensor(data, shape, dtype)
          );
          m.setWeights(weights);
          weights.forEach((w) => w.dispose());

          setVocab(newVocab);
          setModel(m);
          setIsModelReady(true);

          const metrics = lastLogRef.current
            ? { val_loss: lastLogRef.current.val_loss, mae: lastLogRef.current.mae }
            : null;
          if (metrics) setLastMetrics(metrics);

          let saved = false;
          try {
            localStorage.removeItem(VOCAB_KEY);
            localStorage.removeItem(METRICS_KEY);
            try { await tf.io.removeModel(MODEL_KEY); } catch {}
            await m.save(MODEL_KEY);
            localStorage.setItem(VOCAB_KEY, JSON.stringify(newVocab));
            if (metrics) localStorage.setItem(METRICS_KEY, JSON.stringify(metrics));
            saved = true;
          } catch (err) {
            console.error("Delay model: save failed:", err);
          }
          resolve(saved);
        } catch (err) {
          console.error("Delay model: onmessage error:", err);
          setIsTraining(false);
          resolve(false);
        }
      };

      worker.onerror = (err) => {
        console.error("Delay model worker crash:", err);
        worker.terminate();
        workerRef.current = null;
        setIsTraining(false);
        resolve(false);
      };

      worker.postMessage({
        type: 'train',
        payload: { modelType: 'delay', features, labels, epochs: DELAY_EPOCHS, batchSize: 32, validationSplit: 0.2 },
      });
    });
  }, []);

  const predict = useCallback(
    (inputFeatures) => {
      if (!model || !vocab) return null;
      const feature = buildFeature(inputFeatures, vocab);
      const input   = tf.tensor2d([feature]);
      const output  = model.predict(input);
      const raw     = output.dataSync();
      input.dispose();
      output.dispose();
      return {
        depDelay: Math.max(0, Math.round(raw[0] * MAX_DELAY * 10) / 10),
        arrDelay: Math.max(0, Math.round(raw[1] * MAX_DELAY * 10) / 10),
      };
    },
    [model, vocab]
  );

  const computeFeatureImportance = useCallback(() => {
    if (!model || !vocab) return null;
    const { depPorts, arrPorts, acTypes, flightRegs = [] } = vocab;

    let samples;
    if (trainingDataRef.current?.length > 0) {
      samples = trainingDataRef.current.slice(0, 200);
    } else {
      samples = Array.from({ length: 200 }, () => ({
        dep:       depPorts[Math.floor(Math.random() * depPorts.length)]     || "",
        arr:       arrPorts[Math.floor(Math.random() * arrPorts.length)]     || "",
        acType:    acTypes[Math.floor(Math.random() * acTypes.length)]       || "",
        flightReg: flightRegs[Math.floor(Math.random() * flightRegs.length)] || "",
        dayOfWeek: Math.floor(Math.random() * 7),
        month:     Math.floor(Math.random() * 12),
        stdHour:   Math.floor(Math.random() * 24),
        stdMinute: Math.floor(Math.random() * 60),
      }));
    }

    const featureMatrix = samples.map((r) => buildFeature(r, vocab));
    let xs;
    try {
      xs = tf.tensor2d(featureMatrix);
      const basePreds = tf.tidy(() => model.predict(xs).arraySync());

      const importances = FEATURE_NAMES.map((name, fi) => {
        const permuted  = featureMatrix.map((row) => { const copy = [...row]; copy[fi] = Math.random(); return copy; });
        const xsPerm    = tf.tensor2d(permuted);
        const permPreds = tf.tidy(() => model.predict(xsPerm).arraySync());
        xsPerm.dispose();

        const msd = basePreds.reduce((sum, b, i) => {
          const dDep = b[0] - permPreds[i][0];
          const dArr = b[1] - permPreds[i][1];
          return sum + (dDep ** 2 + dArr ** 2) / 2;
        }, 0) / basePreds.length;

        return { feature: name, importance: Math.max(0, parseFloat(msd.toFixed(6))) };
      });

      return importances.sort((a, b) => b.importance - a.importance);
    } catch (err) {
      console.error("computeFeatureImportance error:", err);
      return null;
    } finally {
      xs?.dispose();
    }
  }, [model, vocab]);

  const resetModel = useCallback(async () => {
    workerRef.current?.terminate();
    workerRef.current = null;
    try { await tf.io.removeModel(MODEL_KEY); } catch {}
    try { await tf.io.removeModel(OLD_MODEL_KEY); } catch {}
    localStorage.removeItem(VOCAB_KEY);
    localStorage.removeItem(METRICS_KEY);
    setModel(null);
    setVocab(null);
    setLastMetrics(null);
    setIsModelReady(false);
    setIsTraining(false);
    setTrainingLogs([]);
    lastLogRef.current = null;
    trainingDataRef.current = null;
  }, []);

  return {
    isModelReady,
    isTraining,
    trainingLogs,
    lastMetrics,
    vocab,
    trainModel,
    predict,
    computeFeatureImportance,
    resetModel,
  };
}
